import React, { useState, useEffect } from 'react';
import { Map, TileLayer, Polygon, Marker } from 'react-leaflet';
import L from 'leaflet';
import zonesService from '@services/zones';
import { hasMenuPermission } from '@utils/permissions';
import UserContext from '@components/context/userContext';
import './zoneSelector.css';

const ZoneSelector = ({ 
  value, 
  onChange, 
  disabled = false, 
  placeholder = "Selecionar zona...",
  showCreateButton = true,
  onCreateZone = null
}) => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedZone, setSelectedZone] = useState(null);
  const context = React.useContext(UserContext);

  // Carregar zonas quando o componente monta
  useEffect(() => {
    if (context?.idEmpresa) {
      loadZones();
    }
  }, [context?.idEmpresa]);

  // Atualizar zona selecionada quando value muda
  useEffect(() => {
    console.log('ZoneSelector: useEffect value/zones:', { value, zonesLength: zones.length, zones: zones.map(z => ({ id: z.id, name: z.name })) });
    if (value && zones.length > 0) {
      const zone = zones.find(z => z.id == value || z.id === parseInt(value));
      console.log('ZoneSelector: Zona encontrada:', zone);
      setSelectedZone(zone || null);
    } else if (!value) {
      setSelectedZone(null);
    }
  }, [value, zones]);

  const loadZones = async () => {
    if (!context?.idEmpresa) return;

    setLoading(true);
    setError(null);

    try {
      const response = await zonesService.getZones(context.idEmpresa);
      
      if (response.status === 'success') {
        const zonesData = response.zones || [];
        console.log('ZoneSelector: Zonas carregadas da API:', zonesData);
        setZones(zonesData);
      } else {
        throw new Error(response.message || 'Erro ao carregar zonas');
      }
    } catch (err) {
      console.error('Erro ao carregar zonas:', err);
      setError('Erro ao carregar zonas');
      setZones([]);
    } finally {
      setLoading(false);
    }
  };

  const handleZoneSelect = (zone) => {
    setSelectedZone(zone);
    setIsOpen(false);
    setSearchTerm('');
    onChange(zone.id);
  };

  const handleClear = () => {
    setSelectedZone(null);
    onChange(null);
  };

  const handleCreateNew = () => {
    if (onCreateZone) {
      onCreateZone();
    } else {
      // Redirecionar para a página de zonas
      window.open('/zones', '_blank');
    }
  };

  const filteredZones = zones.filter(zone =>
    zone.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const hasPermission = hasMenuPermission('config:zones', context);

  // Calcular o centro da zona para o minimapa
  const getZoneCenter = (zone) => {
    if (!zone || !zone.coordinates || zone.coordinates.length === 0) return null;
    
    const coords = zone.coordinates;
    let latSum = 0, lngSum = 0;
    
    for (let i = 0; i < coords.length - 1; i++) { // Excluir último ponto (duplicado)
      latSum += coords[i][0];
      lngSum += coords[i][1];
    }
    
    return [
      latSum / (coords.length - 1),
      lngSum / (coords.length - 1)
    ];
  };

  const zoneCenter = selectedZone ? getZoneCenter(selectedZone) : null;

  return (
    <div className="zone-selector-container">
      <div className="zone-selector">
      <div className="zone-selector-input" onClick={() => !disabled && setIsOpen(!isOpen)}>
        <div className="zone-selector-content">
          {selectedZone ? (
            <div className="zone-selected">
              <div 
                className="zone-color-indicator"
                style={{ backgroundColor: selectedZone.color || '#007bff' }}
              />
              <span className="zone-name">{selectedZone.name}</span>
              <span className="zone-area">{selectedZone.area?.toFixed(2)} km²</span>
            </div>
          ) : (
            <span className="zone-placeholder">{placeholder}</span>
          )}
        </div>
        
        <div className="zone-selector-actions">
          {selectedZone && !disabled && (
            <button
              type="button"
              className="zone-clear-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              title="Limpar seleção"
            >
              ✕
            </button>
          )}
          <div className="zone-dropdown-arrow">
            {isOpen ? '▲' : '▼'}
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="zone-dropdown" onClick={(e) => e.stopPropagation()}>
          <div className="zone-search">
            <input
              type="text"
              placeholder="Pesquisar zonas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="zone-search-input"
            />
          </div>

          {loading && (
            <div className="zone-loading">
              <div className="zone-spinner"></div>
              <span>Carregando zonas...</span>
            </div>
          )}

          {error && (
            <div className="zone-error">
              <span>⚠️ {error}</span>
              <button 
                type="button" 
                onClick={loadZones}
                className="zone-retry-btn"
              >
                Tentar novamente
              </button>
            </div>
          )}

          {!loading && !error && (
            <div className="zone-list">
              {filteredZones.length === 0 ? (
                <div className="zone-empty">
                  <span>Nenhuma zona encontrada</span>
                  {hasPermission && showCreateButton && (
                    <button
                      type="button"
                      onClick={handleCreateNew}
                      className="zone-create-btn"
                    >
                      + Criar nova zona
                    </button>
                  )}
                </div>
              ) : (
                filteredZones.map(zone => (
                  <div
                    key={zone.id}
                    className="zone-option"
                    onClick={() => handleZoneSelect(zone)}
                  >
                    <div className="zone-option-content">
                      <div 
                        className="zone-option-color"
                        style={{ backgroundColor: zone.color || '#007bff' }}
                      />
                      <div className="zone-option-info">
                        <span className="zone-option-name">{zone.name}</span>
                        <span className="zone-option-area">
                          {zone.area?.toFixed(2)} km² • {zone.coordinates?.length - 1 || 0} pontos
                        </span>
                      </div>
                    </div>
                    {selectedZone?.id === zone.id && (
                      <div className="zone-option-check">✓</div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {hasPermission && showCreateButton && filteredZones.length > 0 && (
            <div className="zone-create-section">
              <button
                type="button"
                onClick={handleCreateNew}
                className="zone-create-btn"
              >
                + Criar nova zona
              </button>
            </div>
          )}
        </div>
      )}
      </div>

      {/* Minimapa da zona selecionada */}
      {selectedZone && zoneCenter && (
        <div className="zone-minimap">
          <div className="zone-minimap-header">
            <h4>Localização da Zona</h4>
            <span className="zone-minimap-name">{selectedZone.name}</span>
          </div>
          <div className="zone-minimap-container">
            <Map
              center={zoneCenter}
              zoom={13}
              style={{ height: '200px', width: '100%' }}
              zoomControl={false}
              attributionControl={false}
              dragging={false}
              touchZoom={false}
              doubleClickZoom={false}
              scrollWheelZoom={false}
              boxZoom={false}
              keyboard={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <Polygon
                positions={selectedZone.coordinates}
                color={selectedZone.color || '#007bff'}
                fillColor={selectedZone.color || '#007bff'}
                fillOpacity={0.3}
                weight={2}
              />
              <Marker 
                position={zoneCenter}
                icon={L.divIcon({
                  className: 'zone-center-marker',
                  html: '<div style="background-color: #007bff; color: white; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold;">📍</div>',
                  iconSize: [20, 20],
                  iconAnchor: [10, 10]
                })}
              />
            </Map>
          </div>
          <div className="zone-minimap-info">
            <div className="zone-info-item">
              <span className="zone-info-label">Área:</span>
              <span className="zone-info-value">{selectedZone.area?.toFixed(2)} km²</span>
            </div>
            <div className="zone-info-item">
              <span className="zone-info-label">Pontos:</span>
              <span className="zone-info-value">{selectedZone.coordinates?.length - 1 || 0}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoneSelector;
